import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import 'App.css';

import { useDarkMode } from 'context/DarkModeContext';

import Filter from 'components/Filter';
import ProductDrawer from 'components/ProductDrawer';
import ProductGrid from 'components/ProductGrid';
import ProductList from 'components/ProductList';
import Sort from 'components/Sort';

import Logo from 'assets/icon/logo-bk.svg';
import DarkModeLogo from 'assets/icon/logo-wh.svg';
import SeasonTitle from 'assets/icon/season_title-bk.svg';
import DarkModeSeasonTitle from 'assets/icon/season_title-wh.svg';
import BrightnessIcon from 'assets/icon/icon-black_and_white.svg';
import DarkModeBrightnessIcon from 'assets/icon/icon_wh-black_and_white.svg';
import ShuffleIcon from 'assets/icon/icon-shuffle.svg';
import ShuffleIcon2 from 'assets/icon/icon-shuffle-bk-F.svg';
import DarkModeShuffleIcon from 'assets/icon/icon_wh-shuffle.svg';
import DarkModeShuffleIcon2 from 'assets/icon/icon_wh-shuffle-T.svg';
import ScaleIcon from 'assets/icon/icon-range_input.svg';
import DarkModeScaleIcon from 'assets/icon/icon_wh-range_input.svg';
import CloseIcon from 'assets/icon/icon-x_close.svg';
import DarkModeCloseIcon from 'assets/icon/icon_wh-x_close.svg';
import useCheckPC from 'hooks/useCheckPC';
import { shuffleArray } from 'utils/shuffleArray';
import Info from 'components/Info';
import Loading from 'components/Loading';
import LoginPage from 'components/LoginPage';
import Timer from 'components/Timer';

function App() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { isPC, isMobile, isTabletSmall, isTabletLarge } = useCheckPC();
  const [searchParams, setSearchParams] = useSearchParams();

  const [shuffledProducts, setShuffledProducts] = useState([]);

  const [selectedFilter, setSelectedFilter] = useState(searchParams.get('filter') ?? 'All');
  const [selectedSort, setSelectedSort] = useState(searchParams.get('sort') ?? 'Default');
  const [view, setView] = useState(searchParams.get('view') ?? 'IMAGE');
  /** Product Drawer가 열려있는지 확인하는 state **/
  const [isProductDrawerOpen, setIsProductDrawerOpen] = useState(false);
  /** shuffleIcon을 제어하는 state */
  const [shuffleIcon, setShuffleIcon] = useState(ShuffleIcon);
  const [darkShuffleIcon, setDarkShuffleIcon] = useState(DarkModeShuffleIcon);

  const [isFloatingBarOpen, setIsFloatingBarOpen] = useState(false);
  /** moblie header가 열려있는지 확인하는 state **/
  const [isHeaderNavOpen, setIsHeaderNavOpen] = useState(false);
  /** 상품 grid를 제어하는 확인하는 state **/
  const [gridColumns, setGridColumns] = useState(0);
  /** Nav의 Filter, Sort 항목이 열려있는지 확인하는 state **/
  const [navOpen, setNavOpen] = useState({
    title: '',
    isOpen: false,
  });

  /** shuffle Icon 클릭 시, load 애니메이션 적용시켜주는 state */
  const [isShuffled, setIsShuffled] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [auth, setAuth] = useState(localStorage.getItem('auth') ?? '');
  const [products, setProducts] = useState([]);
  const [lookbook, setLookbook] = useState([]);

  const [isFooterVisible, setIsFooterVisible] = useState(true);

  useEffect(() => {
    if (window.history) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (auth) {
      fetch(`https://fw24.thisisneverthat.com/products.json?auth=${auth}`)
        .then(response => response.json())
        .then(data => setProducts(data))
        .then(() => handleShowLoading())
        .catch(error => {
          setAuth('');
          localStorage.removeItem('auth');
        });
      fetch(`https://fw24.thisisneverthat.com/lookbook.json?auth=${auth}`)
        .then(response => response.json())
        .then(data => setLookbook(data))
        .then(() => handleShowLoading())
        .catch(error => {
          setAuth('');
          localStorage.removeItem('auth');
        });
    }
  }, [auth]);

  useEffect(() => {
    if (searchParams.get('product') || searchParams.get('lookbook')) {
      setIsProductDrawerOpen(true);
      // Drawer가 이미 열려있는 경우 무시
      if (!isProductDrawerOpen) {
        setIsProductDrawerOpen(true);
        setTimeout(() => {
          setIsFooterVisible(false);
          setTimeout(() => {
            setIsFooterVisible(true);
          }, 600);
        }, 0);
      }
    } else {
      setIsProductDrawerOpen(false);
    }
    setSelectedFilter(searchParams.get('filter') ?? 'All');
    setSelectedSort(searchParams.get('sort') ?? 'Default');
  }, [searchParams]);

  /** products shuffle */
  useEffect(() => {
    if (products.length > 0 && lookbook.length > 0) {
      const shuffledArray = shuffleArray([...lookbook, ...products]);
      setShuffledProducts(shuffledArray);
    }
  }, [products, lookbook, shuffleIcon]);

  /** scroll Top */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const handleShowLoading = () => {
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 700);

    return () => clearTimeout(timeoutId);
  };

  const openNav = e => {
    e.stopPropagation();
    let title;
    if (e.target.innerText.includes('FILTER')) {
      title = 'FILTER';
    } else if (e.target.innerText.includes('SORT')) {
      title = 'SORT';
    } else if (e.target.innerText.includes('INFO')) {
      title = 'INFO';
    } else {
      title = e.target.innerText;
    }
    setNavOpen({
      title,
      isOpen: true,
    });
  };

  /** PC일 때, filter, sort 메뉴 핸들링 함수 */
  const closeNav = () => {
    setNavOpen({
      title: '',
      isOpen: false,
    });
  };

  /** Grid 사이즈 조절 함수 */
  const handleInputChange = event => {
    let value = parseInt(event.target.value, 10);
    setGridColumns(value);
  };

  /** 모바일일 떼, Header 닫기버튼 핸들링 함수 */
  const toggleHeaderNav = () => setIsHeaderNavOpen(prev => !prev);

  const toggleView = () => {
    setView(prev => (prev === 'IMAGE' ? 'TEXT' : 'IMAGE'));

    setIsFloatingBarOpen(false);

    handleShowLoading();
    (isMobile || isTabletSmall) && toggleHeaderNav();
  };

  useEffect(() => {
    const safeAreaEl = document.querySelector('#safe-area');

    if (auth !== '' && products.length !== 0) {
      if ((isMobile || isTabletSmall) && isProductDrawerOpen) {
        document.querySelector('#root').style.overflow = 'hidden';
        document.querySelector('#root').style.height = '100dvh';
      } else {
        document.querySelector('#root').style.overflow = 'auto';
        document.querySelector('#root').style.height = 'auto';
      }
      if (isPC) {
        if (isProductDrawerOpen) {
          document.querySelector('main').style.minHeight = 'calc(100dvh - 40.8rem)';
        } else {
          document.querySelector('main').style.minHeight = 'calc(100dvh - 31.3rem)';
        }
      }
      if (safeAreaEl) {
        if (isTabletSmall || isTabletLarge) {
          document.querySelector('#safe-area').style.width = '100vw';
        } else {
          document.querySelector('#safe-area').style.width = '0';
        }
      }
    }
  }, [isProductDrawerOpen, isPC, isMobile, isTabletLarge, isTabletSmall, auth, products]);

  const handleShuffleProducts = () => {
    setShuffleIcon(prev => (prev === ShuffleIcon ? ShuffleIcon2 : ShuffleIcon));
    setDarkShuffleIcon(prev => (prev === DarkModeShuffleIcon ? DarkModeShuffleIcon2 : DarkModeShuffleIcon));
    setIsShuffled(prev => !prev);
  };

  const init = () => {
    window.scrollTo(0, 0);
    setSearchParams('');
    setView('IMAGE');
    setIsProductDrawerOpen(false);
    setShuffleIcon(ShuffleIcon);
    setDarkShuffleIcon(DarkModeShuffleIcon);
    setIsFloatingBarOpen(false);
    setIsHeaderNavOpen(false);
    setGridColumns(0);
    setNavOpen({
      title: '',
      isOpen: false,
    });
    handleShowLoading();
  };

  const handleToggleRangeInput = () => {
    setIsFloatingBarOpen(prev => !prev);
  };

  if (auth === '' && products.length === 0) {
    return <LoginPage auth={auth} setAuth={setAuth} setProducts={setProducts} handleShowLoading={handleShowLoading} />;
  }

  return (
    <>
      {isLoading && <Loading products={shuffledProducts} />}
      {/*헤더 영역, state를 한번에 내려주기 위해 헤더 컴포넌트는 따로 만들지 않았음.*/}
      <header style={{ backgroundColor: isHeaderNavOpen && (isMobile || isTabletSmall) ? 'var(--color-bg)' : '' }}>
        <ul className='left-menu'>
          <button onClick={init}>thisisneverthat® FW24 AIRPORT STYLE</button>
          {(isMobile || isTabletSmall) && (
            <button onClick={toggleHeaderNav}>{!isHeaderNavOpen ? <span>MENU</span> : <span>CLOSE</span>}</button>
          )}
        </ul>
        {(isHeaderNavOpen || (!isMobile && !isTabletSmall)) && (
          <ul className='right-menu'>
            {menus.map((menuGroup, groupIndex) => (
              <li key={groupIndex}>
                {menuGroup.map((menu, index) => (
                  <div key={index}>
                    {menu.contents ? (
                      <div>
                        {menu.name === 'FILTER' && (
                          <>
                            {selectedSort !== 'Default' ? (
                              <button onClick={openNav}>
                                {menu.name}
                                {!isMobile && !isTabletSmall ? ',' : ''}
                              </button>
                            ) : (
                              <button onClick={openNav}>
                                {menu.name}({selectedFilter}){!isMobile && !isTabletSmall ? ',' : ''}
                              </button>
                            )}
                          </>
                        )}
                        {menu.name === 'SORT' && (
                          <>
                            {selectedSort === 'Default' || selectedSort === '' ? (
                              <button onClick={openNav}>{menu.name}</button>
                            ) : (
                              <button onClick={openNav}>
                                {menu.name}({selectedSort})
                              </button>
                            )}
                          </>
                        )}
                        {menu.name === 'INFO' && <button onClick={openNav}>{menu.name}</button>}
                      </div>
                    ) : (
                      <>{menu.name === 'VIEW' && <button onClick={toggleView}>{view} VIEW</button>}</>
                    )}
                  </div>
                ))}
              </li>
            ))}
            {navOpen.isOpen && (
              <>
                <div className={'header__navigation'}>
                  <div>
                    <div className={'header__navigation--title'}>
                      <button onClick={closeNav}>{navOpen.title}</button>
                      <button onClick={!isMobile && !isTabletSmall ? closeNav : () => setIsHeaderNavOpen(false)}>
                        CLOSE
                      </button>
                    </div>
                    {(isMobile || isTabletSmall) && (
                      <button onClick={closeNav} className={'back-btn'}>
                        <span>← BACK</span>
                      </button>
                    )}
                    {navOpen.title.includes('FILTER') && (
                      <Filter closeNav={closeNav} closeHeaderNav={toggleHeaderNav} showLoading={handleShowLoading} />
                    )}
                    {navOpen.title === 'SORT' && (
                      <Sort closeNav={closeNav} closeHeaderNav={toggleHeaderNav} showLoading={handleShowLoading} />
                    )}
                    {navOpen.title === 'INFO' && <Info />}
                  </div>
                </div>
              </>
            )}
          </ul>
        )}
        {(navOpen.isOpen || isHeaderNavOpen) && (
          <div
            className={'dim'}
            onClick={
              isPC
                ? closeNav
                : () => {
                    setIsHeaderNavOpen(false);
                    setNavOpen(false);
                  }
            }
          ></div>
        )}
      </header>
      <main
        style={{
          width: `${
            !isMobile && !isTabletSmall && isProductDrawerOpen && view === 'IMAGE'
              ? 'calc(50% - 1.8rem)'
              : 'calc(100% - 2.4rem)'
          }`,
        }}
      >
        {shuffledProducts.length > 0 && isProductDrawerOpen && (
          <ProductDrawer
            products={shuffledProducts}
            handleClose={() => {
              setTimeout(() => {
                setIsFooterVisible(false);
                setTimeout(() => {
                  setIsFooterVisible(true);
                }, 600);
              }, 0);

              let productCardId = searchParams.get('product')
                ? `${searchParams.get('product')}-${searchParams.get('color')}`
                : searchParams.get('lookbook');

              if (searchParams.get('sort')) productCardId += '-sorted';
              const newSearchParams = {};
              if (searchParams.get('filter')) newSearchParams['filter'] = searchParams.get('filter');
              if (searchParams.get('sort')) newSearchParams['sort'] = searchParams.get('sort');
              setSearchParams(newSearchParams);
              if (view === 'TEXT' || searchParams.get('filter') === 'Lookbook') {
                return;
              }

              if (document.getElementById(productCardId) === null) {
                const filterProduct = products.filter((product, i) => {
                  return searchParams.get('name') === product.name && product.colour === searchParams.get('color');
                });
                productCardId = `${filterProduct[0].code}-${searchParams.get('color')}`;
              }

              setTimeout(() => {
                document.getElementById(productCardId).scrollIntoView({ block: 'center' });
              }, 200);
            }}
          />
        )}
        {view === 'IMAGE' && (
          <ProductGrid
            products={shuffledProducts}
            lookbook={lookbook}
            setIsProductDrawerOpen={setIsProductDrawerOpen}
            gridColumns={gridColumns}
            filter={selectedFilter}
            sort={selectedSort}
            closeNav={closeNav}
            isProductDrawerOpen={isProductDrawerOpen}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isShuffled={isShuffled}
          />
        )}
        {view === 'TEXT' && (
          <ProductList
            products={[...products, ...lookbook]}
            lookbook={lookbook}
            setIsProductDrawerOpen={setIsProductDrawerOpen}
            filter={selectedFilter}
            sort={selectedSort}
            isLoading={isLoading}
          />
        )}
      </main>
      <footer className={`${isProductDrawerOpen ? 'drawer-open' : ''} ${isFooterVisible ? 'loaded' : ''}`}>
        <div>
          {darkMode ? <img src={DarkModeLogo} alt={'logo'} /> : <img src={Logo} alt={'logo'} />}
          {darkMode ? (
            <img src={DarkModeSeasonTitle} alt={'seasonTitle'} />
          ) : (
            <img src={SeasonTitle} alt={'seasonTitle'} />
          )}
        </div>
        <div>
          <div>
            <Timer />
          </div>
          <div>
            <nav className={'nav-footer'}>
              <ul className={'connect'}>
                <li>
                  <span>CONNECT US</span>
                </li>
                <li>
                  <a href='https://thisisneverthat.com/' target='_blank' rel='noreferrer'>
                    <span>Store</span>
                  </a>
                </li>
                <li>
                  <a href='https://www.instagram.com/thisisneverthat/' target='_blank' rel='noreferrer'>
                    <span>Instagram</span>
                  </a>
                </li>
                <li>
                  <a href='https://www.youtube.com/Thisisneverthat' target='_blank' rel='noreferrer'>
                    <span>Youtube</span>
                  </a>
                </li>
              </ul>
              <ul className={'contact'}>
                <li>
                  <span className={''}>CONTACT US</span>
                </li>
                <li>
                  <a href='mailto:info@thisisneverthat.com'>
                    <span>info@thisisneverthat.com</span>
                  </a>
                </li>
              </ul>
              <span className={'year'}>©2024</span>
            </nav>
          </div>
        </div>
      </footer>

      {/* floating nav */}
      {((isMobile || isTabletSmall) && isProductDrawerOpen) || (
        <div className={'floating-bar'}>
          <div className={'floating-bar__menu'}>
            <button onClick={() => toggleDarkMode()}>
              {darkMode ? (
                <img src={DarkModeBrightnessIcon} alt={'brightness'} />
              ) : (
                <img src={BrightnessIcon} alt={'brightness'} />
              )}
            </button>
            {view === 'IMAGE' && (
              <button onClick={handleShuffleProducts}>
                {darkMode ? <img src={darkShuffleIcon} alt={'shuffle'} /> : <img src={shuffleIcon} alt={'shuffle'} />}
              </button>
            )}
            {view === 'IMAGE' && (
              <div className={'scale'}>
                <button onClick={handleToggleRangeInput}>
                  {!isFloatingBarOpen ? (
                    darkMode ? (
                      <img src={DarkModeScaleIcon} alt={'scale'} />
                    ) : (
                      <img src={ScaleIcon} alt={'scale'} />
                    )
                  ) : darkMode ? (
                    <img src={DarkModeCloseIcon} alt={'scale'} />
                  ) : (
                    <img src={CloseIcon} alt={'close'} />
                  )}
                </button>
                {isFloatingBarOpen && view === 'IMAGE' && (
                  <div className={'range-input'}>
                    <input type='range' min={0} max={2} step={1} value={gridColumns} onChange={handleInputChange} />
                    <div className={'thumb'} style={{ left: gridColumns * 57 }}></div>
                    <div className={'line'}></div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div id={'safe-area'}></div>
          {isFloatingBarOpen && <div className={'dim'} onClick={() => setIsFloatingBarOpen(false)}></div>}
        </div>
      )}
    </>
  );
}

export default App;

const menus = [
  [
    { name: 'FILTER', url: '', contents: true },
    { name: 'SORT', url: '', contents: true },
  ],
  [{ name: 'VIEW', url: '', contents: false }],
  [{ name: 'INFO', url: '', contents: true }],
];
